import React, { useState, useEffect, useCallback } from 'react';
import { Users, Building2, BarChart2, LogOut, ChevronLeft, Search, AlertTriangle, CheckCircle, ImageOff, SlidersHorizontal, Download, FlaskConical, FileText } from 'lucide-react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { StorageImage } from './StorageImage';
import { ImageZoomOverlay } from './ImageZoomOverlay';
import RegressionTestTab from './RegressionTestTab';
import { DateRangePicker } from './DateRangePicker';
import { exportCSV, formatSerial, isIOS as isIOSDevice } from './utils/format';
import { IMAGE_MODE_LABEL, IMAGE_MODE_STYLE, FEEDBACK_LABEL, FEEDBACK_STYLE, DEPARTMENT_OPTIONS } from './constants';
import { RegisterReferenceButton, fetchInspectionImages, imagesToHtml, openBlankTab, openTabWithContent } from './AdminPanel';
import { runWithConcurrency } from './utils/concurrencyPool';
import { usePendingPdfShare } from './utils/pdfShare';
import { AUTO_PRINT_SCRIPT } from './utils/printAutoScript';
import { isBlurryImage, blurryImageBadgeHtml, BLURRY_IMAGE_LABEL } from './utils/imageSharpness';
import { jstMonthStart } from './utils/jstMonth';

function getLatestFeedback(feedbacks) {
  if (!feedbacks || feedbacks.length === 0) return null;
  const latest = [...feedbacks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  return { verdict: latest.verdict, comment: latest.comment || null };
}

async function downloadInspectionImage(path, serial, index) {
  const { data, error } = await supabase.storage.from('pm-inspection-images').download(path);
  if (error || !data) return;
  const ext = path.split('.').pop() || 'jpg';
  const filename = `PM_${formatSerial(serial)}_${index}.${ext}`;
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// 照合結果カード1件分（判定バッジ＋詳細テーブル）のHTMLを構築する。
// openInspectionPdf（個別）・openBulkInspectionPdf（検索結果全件）の両方から共用する。
// SuperAdmin向け項目（User-Agent等）はAdminPanel.js（CompanyAdmin向け）とは異なるため、
// カード自体はこちらで独自に構築する（画像取得・保存は共通ヘルパーをAdminPanel.jsから流用）
function buildInspectionCardHtml(insp, imgHtml = '') {
  const productName = insp.label_product_name || '—';
  const serial = formatSerial(insp.serial);
  const status = insp.match_status || 'SAFE';
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const color = status === 'SAFE' ? '#2563eb' : status === 'DANGER' ? '#dc2626' : '#6b7280';
  const label = status === 'SAFE' ? '照合OK' : status === 'DANGER' ? '要確認' : 'エラー';
  const tdH = 'padding:3px 8px;background:#f8fafc;font-weight:bold;width:80px;border-bottom:1px solid #e2e8f0;font-size:10px';
  const tdV = 'padding:3px 8px;border-bottom:1px solid #e2e8f0;font-size:10px';
  let detailHtml = `<table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0"><tbody>`;
  detailHtml += `<tr><td style="${tdH}">商品名</td><td style="${tdV}">${esc(productName)}</td></tr>`;
  if (insp.detected_food_visual) detailHtml += `<tr><td style="${tdH}">目視識別</td><td style="${tdV}">${esc(insp.detected_food_visual)}</td></tr>`;
  if (insp.reason) detailHtml += `<tr><td style="${tdH}">判定理由</td><td style="${tdV}">${esc(insp.reason)}</td></tr>`;
  if (insp.model_used) detailHtml += `<tr><td style="${tdH}">使用モデル</td><td style="${tdV}">${esc(insp.model_used)}</td></tr>`;
  if (insp.device_type || insp.browser_type) detailHtml += `<tr><td style="${tdH}">端末</td><td style="${tdV}">${[insp.device_type, insp.browser_type].filter(Boolean).map(esc).join(' / ')}</td></tr>`;
  if (insp.is_standalone != null) detailHtml += `<tr><td style="${tdH}">動作モード</td><td style="${tdV}">${insp.is_standalone ? 'ホーム画面から起動' : 'ブラウザで表示'}</td></tr>`;
  detailHtml += `<tr><td style="${tdH}">検査日時</td><td style="${tdV}">${insp.inspected_at ? new Date(insp.inspected_at).toLocaleString('ja-JP') : '—'}</td></tr>`;
  detailHtml += `<tr><td style="${tdH}">担当者</td><td style="${tdV}">${esc(insp.user_email || '—')}</td></tr>`;
  if (insp.user_agent) detailHtml += `<tr><td style="${tdH}">User-Agent</td><td style="${tdV};font-size:8px;word-break:break-all">${esc(insp.user_agent)}</td></tr>`;
  detailHtml += `</tbody></table>`;
  return `
<div style="page-break-inside:avoid;border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin-bottom:12px;background:#fff">
<div style="margin-bottom:8px;display:flex;align-items:center;gap:8px">
${serial ? `<span style="font-size:10px;color:#64748b;font-family:monospace">${serial}</span>` : ''}
<span style="display:inline-block;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:bold;color:#fff;background:${color}">${label}</span>${blurryImageBadgeHtml(insp.image_quality)}
<span style="font-size:14px;font-weight:bold">${esc(productName)}</span></div>
<div style="display:flex;gap:12px;margin-bottom:8px">${imgHtml ? `<div style="flex:0 0 200px">${imgHtml}</div>` : ''}
<div style="flex:1;min-width:0">${detailHtml}</div></div>
</div>`;
}

// 従来このPDFはiOS対応が無く、window.openで開いた別タブに戻る手段がなくなる
// （PWAで顕著）問題を抱えていた。AdminPanel.js・openBulkInspectionPdfと同じ
// iOS/html2pdf.js分岐を追加する（保存方式はonIOSPdfReady・usePendingPdfShare参照）
async function openInspectionPdf(insp, { exporterEmail = '―', onIOSPdfReady } = {}) {
  const isIOS = isIOSDevice();
  // 画像ダウンロード（下のawait）より前にタブを開いておく（openBlankTab参照。
  // window.open()を非同期処理の後に呼ぶとブラウザにポップアップとしてブロックされうる）
  const w = isIOS ? null : openBlankTab();
  if (!isIOS && !w) return;
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const images = await fetchInspectionImages(insp);
  const cardHtml = buildInspectionCardHtml(insp, await imagesToHtml(images));
  const productName = insp.label_product_name || '—';
  const serial = formatSerial(insp.serial);
  const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><title>照合結果</title>
<style>body{font-family:'Hiragino Sans','Meiryo',sans-serif;padding:20px;font-size:11px;color:#1e293b;background:#f8fafc}@media print{@page{margin:10mm}body{background:#fff}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
</head><body>
<div style="margin-bottom:10px"><h2 style="font-size:16px;margin:0;color:#4f46e5">ProductMatcher 照合レポート</h2>
<span style="font-size:11px;color:#64748b">出力者: ${esc(exporterEmail)} / 出力: ${dateStr}</span></div>
${cardHtml}</body></html>`;

  if (isIOS) {
    const html2pdf = (await import('html2pdf.js')).default;
    const worker = html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(html, 'string');
    const blob = await worker.outputPdf('blob');
    await onIOSPdfReady(blob, `PM_照合結果_${serial || ''}${productName}`);
    return;
  }
  openTabWithContent(w, html + AUTO_PRINT_SCRIPT);
}

// 検査履歴タブの検索結果を全件まとめて1つのPDFにする。個別PDFと同様、各検査の画像も
// 含める。runWithConcurrency（同時実行数を制限できる既存ユーティリティ）で画像取得を
// スロットルする。個別PDF（このファイルの既存実装）はiOS対応が無いが、複数件をまとめて
// 開く新機能ではwindow.openがiOS PWAで戻れなくなる問題がより深刻なため、AdminPanel.js
// の個別PDFと同じiOS/html2pdf.js分岐をこちらには追加する
async function openBulkInspectionPdf(inspections, { exporterEmail = '―', onIOSPdfReady } = {}) {
  if (!inspections || inspections.length === 0) return;
  const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  // iOSはhtml2pdf.js直接ダウンロード、非iOSは画像ダウンロード（下のrunWithConcurrency、
  // 検索結果が多いと数秒〜十数秒かかりうる）より前にタブを開いておく（openBlankTab参照）
  const isIOS = isIOSDevice();
  const w = isIOS ? null : openBlankTab();
  if (!isIOS && !w) return;

  const settled = await runWithConcurrency(inspections, insp => fetchInspectionImages(insp), { concurrency: 4 });

  const cardsHtml = (await Promise.all(settled.map(async entry => {
    const insp = entry.item;
    const images = entry.status === 'fulfilled' ? entry.value : [];
    return buildInspectionCardHtml(insp, await imagesToHtml(images));
  }))).join('');

  const safeCount = inspections.filter(i => i.match_status === 'SAFE').length;
  const dangerCount = inspections.filter(i => i.match_status === 'DANGER').length;
  const errorCount = inspections.filter(i => i.match_status === 'ERROR').length;

  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const fileDate = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const filename = `PM_検査履歴一覧_${fileDate}`;

  const html = `<!DOCTYPE html><html lang="ja"><head><meta charset="UTF-8"><title>${filename}</title>
<style>body{font-family:'Hiragino Sans','Meiryo',sans-serif;padding:20px;font-size:11px;color:#1e293b;background:#f8fafc}@media print{@page{margin:10mm}body{background:#fff}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style>
</head><body>
<div style="margin-bottom:10px">
<h2 style="font-size:16px;margin:0;color:#4f46e5">ProductMatcher 検査履歴一覧</h2>
<div style="font-size:11px;color:#64748b;margin-top:2px">出力者: ${esc(exporterEmail)} / 出力: ${dateStr}</div>
<div style="margin-top:4px;font-size:11px">合計: <b>${inspections.length}件</b>　<span style="color:#2563eb">照合OK: <b>${safeCount}件</b></span>　<span style="color:#dc2626">要確認: <b>${dangerCount}件</b></span>　${errorCount > 0 ? `<span style="color:#6b7280">エラー: <b>${errorCount}件</b></span>` : ''}</div>
</div>
${cardsHtml}
${AUTO_PRINT_SCRIPT}</body></html>`;

  if (isIOS) {
    const html2pdf = (await import('html2pdf.js')).default;
    const printHtml = html.replace(/<script>.*?<\/script>/g, '');
    const worker = html2pdf().set({
      margin: 10,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(printHtml, 'string');
    const blob = await worker.outputPdf('blob');
    await onIOSPdfReady(blob, filename);
    return;
  }
  openTabWithContent(w, html);
}

function DepartmentLabelsEditor({ companyId, currentLabels, onSaved, canEdit }) {
  const [labels, setLabels] = useState(currentLabels || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLabels(currentLabels || {}); }, [currentLabels]);
  const setLabel = (dept, value) => { setLabels(prev => { const next = { ...prev }; const trimmed = value.trim(); if (trimmed === '') delete next[dept]; else next[dept] = trimmed; return next; }); };
  const save = async () => {
    setSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/update-company', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` }, body: JSON.stringify({ companyId, field: 'department_labels', value: Object.keys(labels).length > 0 ? labels : null }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { alert(data.error || '保存に失敗しました'); }
    setSaving(false);
    if (res.ok) onSaved?.();
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">部門名の表示をカスタマイズできます。</p>
      <div className="border rounded-lg overflow-hidden divide-y">
        {DEPARTMENT_OPTIONS.filter(d => d).map(dept => (
          <div key={dept} className="flex items-center justify-between px-3 py-2 gap-2">
            <span className="text-xs text-slate-700 w-20 shrink-0">{dept}</span>
            <input type="text" maxLength={20} placeholder={dept} value={labels[dept] ?? ''} onChange={e => setLabel(dept, e.target.value)} disabled={!canEdit} className="flex-1 px-2 py-1 border rounded text-xs disabled:bg-slate-50" />
          </div>
        ))}
      </div>
      {canEdit && <button onClick={save} disabled={saving} className="w-full py-2 bg-slate-700 text-white text-sm font-bold rounded disabled:bg-slate-300">{saving ? '保存中...' : '保存する'}</button>}
    </div>
  );
}

function SuperAdminPanel({ onClose, onSignOut }) {
  const signOut = onSignOut;
  const { profile } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [usageSummary, setUsageSummary] = useState([]);
  const [emailRegs, setEmailRegs] = useState([]);
  const [tab, setTab] = useState('companies');
  const [loading, setLoading] = useState(true);
  const [zoomImage, setZoomImage] = useState(null);

  const [newCompanyName, setNewCompanyName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingLimits, setEditingLimits] = useState({});
  const [expandedDeptLabelsId, setExpandedDeptLabelsId] = useState(null);

  // 検査履歴検索
  const [inspections, setInspections] = useState([]);
  const [searchSerial, setSearchSerial] = useState('');
  const [searchCompanyId, setSearchCompanyId] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchProductName, setSearchProductName] = useState('');
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [bulkPdfLoading, setBulkPdfLoading] = useState(false);
  const { pendingPdf, shareOrDefer, sharePendingPdf } = usePendingPdfShare();
  const [selectedInspection, setSelectedInspection] = useState(null);

  // システム設定
  const [settings, setSettings] = useState({ magic_link_required: false, pm_image_storage_enabled: false });
  const [savingKey, setSavingKey] = useState(null);

  const fetchCompanies = useCallback(async () => {
    const { data } = await supabase.from('companies').select('*').order('created_at', { ascending: false });
    setCompanies(data || []);
  }, []);

  const fetchUsageSummary = useCallback(async () => {
    const firstDay = jstMonthStart();
    const { data } = await supabase
      .from('pm_usage_daily')
      .select('company_id, match_count, companies(name)')
      .gte('date', firstDay);

    if (data) {
      const map = {};
      data.forEach(row => {
        if (!map[row.company_id]) map[row.company_id] = { name: row.companies?.name, total: 0 };
        map[row.company_id].total += row.match_count;
      });
      setUsageSummary(Object.entries(map).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.total - a.total));
    }
  }, []);

  const [userRoleMap, setUserRoleMap] = useState({});
  const fetchEmailRegs = useCallback(async () => {
    const { data } = await supabase
      .from('pm_email_registrations')
      .select('*')
      .order('last_seen', { ascending: false });
    // email_registrations.match_count は companyId無しでAPIを呼んだ回数（回帰テスト等）しか
    // カウントしておらず、実際の検査件数とは大きく乖離する（例: 会社所属ユーザーの本番検査は
    // companyId付きでincrement_usageを通るためここでは常に0のまま）。
    // inspectionsテーブルからメール別の実件数を集計して上書きする。
    const { data: inspRows } = await supabase.from('pm_inspections').select('user_email');
    const counts = {};
    (inspRows || []).forEach(r => { if (r.user_email) counts[r.user_email] = (counts[r.user_email] || 0) + 1; });
    setEmailRegs((data || []).map(row => ({ ...row, match_count: counts[row.email] || 0 })));
    const { data: profiles } = await supabase.from('user_profiles').select('email, role, companies(name)');
    if (profiles) {
      const map = {};
      profiles.forEach(p => { if (p.email) map[p.email] = { role: p.role, company: p.companies?.name }; });
      setUserRoleMap(map);
    }
  }, []);

  const fetchInspections = useCallback(async () => {
    setSearchLoading(true);
    let query = supabase
      .from('pm_inspections')
      .select('serial, id, company_id, user_email, store_name, dept_name, lot_number, label_product_name, detected_food_visual, match_status, reason, image_urls, inspected_at, image_mode, model_used, device_type, user_agent, browser_type, is_standalone, analyze_ms, app_version, image_quality, inspection_feedbacks:pm_inspection_feedbacks(verdict, comment, created_at)')
      .order('inspected_at', { ascending: false })
      .limit(100);
    if (searchSerial.trim()) query = query.eq('serial', parseInt(searchSerial));
    if (searchCompanyId) query = query.eq('company_id', searchCompanyId);
    if (searchStatus) query = query.eq('match_status', searchStatus);
    if (searchProductName.trim()) query = query.ilike('label_product_name', `%${searchProductName.trim()}%`);
    if (searchDateFrom) query = query.gte('inspected_at', searchDateFrom + 'T00:00:00+09:00');
    if (searchDateTo) query = query.lte('inspected_at', searchDateTo + 'T23:59:59+09:00');
    const { data } = await query;
    setInspections(data || []);
    setSearchLoading(false);
  }, [searchSerial, searchCompanyId, searchStatus, searchProductName, searchDateFrom, searchDateTo]);

  const fetchSettings = useCallback(async () => {
    const { data } = await supabase.from('system_settings').select('key, value');
    if (data) {
      const map = {};
      data.forEach(r => { map[r.key] = r.value === 'true' || r.value === true; });
      setSettings(prev => ({ ...prev, ...map }));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCompanies(), fetchUsageSummary(), fetchEmailRegs(), fetchSettings()])
      .finally(() => setLoading(false));
  }, [fetchCompanies, fetchUsageSummary, fetchEmailRegs, fetchSettings]);

  const updateSetting = async (key, value) => {
    setSavingKey(key);
    const { error } = await supabase.from('system_settings').upsert({ key, value: String(value) });
    if (error) console.error('[updateSetting] error:', error.message);
    setSettings(prev => ({ ...prev, [key]: value }));
    setSavingKey(null);
  };

  const createCompany = async () => {
    if (!newCompanyName.trim() || !adminEmail.trim()) return;
    setCreating(true);
    if (companies.some(c => c.name === newCompanyName.trim())) { alert('同名の企業が既に存在します'); setCreating(false); return; }
    // 「このメールアドレスは既に別の企業に所属しているか」の事前チェックは、
    // 存在しない'profiles'テーブル（正しくは'user_profiles'）を参照しており常に
    // エラーになっていたうえ、user_profilesにはそもそもemail列が無くクライアント
    // 側（anonキー）からは照合できない（emailはauth.usersにしか無く、admin権限の
    // listUsers()経由でしか引けない）。この重複判定は/api/invite側が
    // auth.admin.listUsers()で既に正しく行っており「このメールアドレスは既に
    // 登録されています」を返すため、その結果が返るまで企業作成をロールバック
    // できるようにする（下記参照）
    const { data: company, error: cErr } = await supabase
      .from('companies')
      .insert({ name: newCompanyName.trim() })
      .select()
      .single();

    if (cErr) { alert('企業作成エラー: ' + cErr.message); setCreating(false); return; }

    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const iRes = await fetch('/api/invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentSession?.access_token}`,
      },
      body: JSON.stringify({
        email: adminEmail.trim(),
        companyId: company.id,
        role: 'admin',
        name: newCompanyName.trim() + ' 管理者'
      })
    });
    const iData = await iRes.json();
    setCreating(false);
    if (iData.error) {
      // 招待が失敗した場合（メールアドレスが既に登録済み等）、管理者が1人も
      // 招待されていない空の企業がそのまま残ってしまうのを避けるため、
      // 直前に作成した企業をロールバックする
      await supabase.from('companies').delete().eq('id', company.id);
      alert('招待エラー: ' + iData.error + '（作成した企業はロールバックしました）');
      fetchCompanies();
    } else {
      setNewCompanyName('');
      setAdminEmail('');
      alert('企業を作成し、管理者へ招待メールを送信しました');
      fetchCompanies();
    }
  };

  const exportInspectionsCSV = () => {
    const headers = ['シリアル', '企業', '店舗', '部門', 'ロット番号', '商品名(ラベル)', '目視識別', '判定', '判定理由', '検査日時', '担当者メール', '検査モード', '使用モデル', '端末', 'ブラウザ', '動作モード', 'User-Agent', 'フィードバック', 'FBコメント', '判定時間(秒)'];
    const rows = inspections.map(r => {
      const fb = getLatestFeedback(r.inspection_feedbacks);
      return [
        formatSerial(r.serial),
        companies.find(c => c.id === r.company_id)?.name ?? '',
        r.store_name ?? '', r.dept_name ?? '', r.lot_number ?? '',
        r.label_product_name ?? '', r.detected_food_visual ?? '',
        r.match_status ?? '', r.reason ?? '',
        r.inspected_at ? new Date(r.inspected_at).toLocaleString('ja-JP') : '',
        r.user_email ?? '',
        r.image_mode === 'speed' ? '速度優先' : r.image_mode === 'accuracy' ? '精度優先' : '',
        r.model_used ?? '',
        r.device_type ?? '',
        r.browser_type ?? '',
        r.is_standalone == null ? '' : (r.is_standalone ? 'ホーム画面から起動' : 'ブラウザで表示'),
        r.user_agent ?? '',
        fb ? FEEDBACK_LABEL[fb.verdict] ?? fb.verdict : '',
        fb?.comment ?? '',
        r.analyze_ms != null ? (r.analyze_ms / 1000).toFixed(2) : '',
      ];
    });
    const date = new Date().toISOString().slice(0, 10);
    exportCSV(headers, rows, `PM_検査履歴_全社_${date}.csv`);
  };

  const updateCompanyField = async (companyId, field, value) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/update-company', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      body: JSON.stringify({ companyId, field, value }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.error || '更新に失敗しました'); return false; }
    return true;
  };

  const toggleActive = async (company) => {
    if (await updateCompanyField(company.id, 'is_active', !company.is_active)) fetchCompanies();
  };

  const toggleAllowOverage = async (company) => {
    if (await updateCompanyField(company.id, 'allow_overage', !company.allow_overage)) fetchCompanies();
  };

  const updateCompanyLimit = async (companyId, field, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    if (await updateCompanyField(companyId, field, num)) {
      setEditingLimits(prev => { const n = { ...prev }; delete n[`${companyId}_${field}`]; return n; });
      fetchCompanies();
    }
  };

  // トグルスイッチUI
  const Toggle = ({ value, onChange, disabled }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${value ? 'bg-slate-700' : 'bg-slate-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <header className="bg-slate-800 text-white px-4 py-3 flex items-center gap-3">
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 font-bold">SuperAdmin</div>
        <button onClick={signOut} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
          <LogOut className="w-4 h-4" /> ログアウト
        </button>
      </header>

      <div className="flex border-b bg-white overflow-x-auto">
        {[
          { key: 'companies', label: '契約企業', icon: Building2 },
          { key: 'usage', label: '使用量', icon: BarChart2 },
          { key: 'emails', label: '登録メール', icon: Users },
          { key: 'inspections', label: '検査履歴', icon: Search },
          { key: 'regression', label: 'テスト', icon: FlaskConical },
          { key: 'settings', label: '設定', icon: SlidersHorizontal },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-shrink-0 flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              tab === key ? 'border-slate-700 text-slate-800' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 max-w-lg mx-auto w-full">
        {loading ? (
          <div className="text-center py-10 text-slate-400">読み込み中...</div>
        ) : (
          <>
            {tab === 'companies' && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">新規企業を追加</p>
                  <input
                    type="text"
                    placeholder="企業名"
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <input
                    type="email"
                    placeholder="管理者メールアドレス"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <button
                    onClick={createCompany}
                    disabled={creating || !newCompanyName.trim() || !adminEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim())}
                    className="w-full py-2 bg-slate-700 text-white text-sm font-bold rounded disabled:bg-slate-300"
                  >
                    {creating ? '作成中...' : '企業を作成して招待'}
                  </button>
                </div>

                {/* 企業一覧 */}
                <div className="space-y-2">
                  {companies.map(c => (
                    <div key={c.id} className={`p-3 rounded-lg border ${c.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-slate-400">
                            {new Date(c.created_at).toLocaleDateString('ja-JP')}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleActive(c)}
                          className={`text-xs px-2 py-1 rounded ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}
                        >
                          {c.is_active ? '有効' : '無効'}
                        </button>
                      </div>
                      <div className="flex gap-3 text-xs text-slate-500">
                        <label className="flex items-center gap-1">
                          月上限
                          <input
                            type="number"
                            min="0"
                            value={editingLimits[`${c.id}_monthly_limit`] ?? c.monthly_limit ?? 0}
                            onChange={e => { setEditingLimits(prev => ({ ...prev, [`${c.id}_monthly_limit`]: e.target.value })); updateCompanyLimit(c.id, 'monthly_limit', e.target.value); }}
                            className="w-20 px-1 py-0.5 border rounded text-xs"
                          />
                          件
                        </label>
                        <label className="flex items-center gap-1">
                          管理者上限
                          <input
                            type="number"
                            min="0"
                            value={editingLimits[`${c.id}_max_admin`] ?? c.max_admin ?? 0}
                            onChange={e => { setEditingLimits(prev => ({ ...prev, [`${c.id}_max_admin`]: e.target.value })); updateCompanyLimit(c.id, 'max_admin', e.target.value); }}
                            className="w-12 px-1 py-0.5 border rounded text-xs"
                          />
                          名
                        </label>
                        <span className="text-slate-300 self-center">（0=無制限）</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                        <span>超過後も継続（後払い）</span>
                        <Toggle
                          value={!!c.allow_overage}
                          onChange={() => toggleAllowOverage(c)}
                        />
                      </div>
                      <button
                        onClick={() => setExpandedDeptLabelsId(expandedDeptLabelsId === c.id ? null : c.id)}
                        className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1 py-1 border-t"
                      >
                        <SlidersHorizontal className="w-3 h-3" />
                        部門名表示設定{expandedDeptLabelsId === c.id ? 'を閉じる' : 'を編集'}
                      </button>
                      {expandedDeptLabelsId === c.id && (
                        <div className="mt-2 pt-2 border-t">
                          <DepartmentLabelsEditor companyId={c.id} currentLabels={c.department_labels} canEdit={true} onSaved={fetchCompanies} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'usage' && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 mb-3">今月の企業別照合回数</p>
                {usageSummary.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">データなし</p>
                ) : (
                  usageSummary.map(row => {
                    const company = companies.find(c => c.id === row.id);
                    const limit = company?.monthly_limit || 0;
                    const overage = limit > 0 ? Math.max(0, row.total - limit) : 0;
                    const pct = limit > 0 ? Math.min(100, Math.round((row.total / limit) * 100)) : null;
                    return (
                      <div key={row.id} className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{row.name}</span>
                          <div className="flex items-center gap-2">
                            {overage > 0 && (
                              <span className="text-xs font-bold px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                                超過 +{overage.toLocaleString()}回
                              </span>
                            )}
                            <span className="font-bold text-slate-700">{row.total.toLocaleString()} 回</span>
                          </div>
                        </div>
                        {pct !== null && (
                          <div>
                            <div className="flex justify-between text-xs mb-0.5">
                              <span className={pct >= 100 ? 'text-orange-600 font-bold' : pct >= 90 ? 'text-red-500 font-bold' : pct >= 70 ? 'text-orange-500' : 'text-slate-400'}>
                                {pct}%
                              </span>
                              <span className="text-slate-300">上限 {limit.toLocaleString()}件</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-orange-500' : pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-indigo-400'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {tab === 'emails' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-slate-500">メール登録ユーザー（{emailRegs.length}件）</p>
                  <button onClick={fetchEmailRegs} className="text-xs text-slate-400 hover:text-slate-600">更新</button>
                </div>
                {emailRegs.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">登録なし</p>
                ) : (
                  emailRegs.map(row => (
                    <div key={row.email} className="p-3 bg-white rounded-lg border">
                      <div className="font-medium text-sm break-all">
                        {row.email}
                        {userRoleMap[row.email]?.role === 'superadmin' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-white">SuperAdmin</span>}
                        {userRoleMap[row.email]?.role === 'admin' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-indigo-600 text-white">Admin</span>}
                      </div>
                      {userRoleMap[row.email]?.company && <div className="text-xs text-slate-500 mt-0.5">{userRoleMap[row.email].company}</div>}
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs text-slate-400">
                          初回: {new Date(row.first_seen).toLocaleDateString('ja-JP')}
                          {' · '}
                          最終: {new Date(row.last_seen).toLocaleDateString('ja-JP')}
                        </div>
                        <span className="text-xs font-bold text-indigo-700">検査 {row.match_count.toLocaleString()} 回</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {tab === 'inspections' && (
              <div className="space-y-3">
                {/* 検索フォーム */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">検査履歴を検索（最大100件）</p>
                  <input
                    type="number"
                    placeholder="シリアル番号（例: 1234）"
                    value={searchSerial}
                    onChange={(e) => setSearchSerial(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <select value={searchCompanyId} onChange={(e) => setSearchCompanyId(e.target.value)} className="w-full p-2 border rounded text-sm">
                    <option value="">企業（全件）</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input type="text" placeholder="品名で検索" value={searchProductName} onChange={(e) => setSearchProductName(e.target.value)} className="w-full p-2 border rounded text-sm" />
                  <select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)} className="w-full p-2 border rounded text-sm">
                    <option value="">判定（全件）</option>
                    <option value="SAFE">SAFE</option>
                    <option value="DANGER">DANGER</option>
                    <option value="ERROR">ERROR</option>
                  </select>
                  <DateRangePicker
                    from={searchDateFrom}
                    to={searchDateTo}
                    onChange={({ from, to }) => { setSearchDateFrom(from); setSearchDateTo(to); }}
                  />
                  <button
                    onClick={fetchInspections}
                    disabled={searchLoading}
                    className="w-full py-2 bg-slate-700 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    {searchLoading ? '検索中...' : '検索'}
                  </button>
                </div>

                {/* 検索結果 */}
                {inspections.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={exportInspectionsCSV}
                      className="w-full py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" /> CSV ({inspections.length}件)
                    </button>
                    <button
                      disabled={bulkPdfLoading}
                      onClick={async () => { setBulkPdfLoading(true); try { await openBulkInspectionPdf(inspections, { exporterEmail: profile?.email, onIOSPdfReady: shareOrDefer }); } finally { setBulkPdfLoading(false); } }}
                      className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2"
                    >
                      <FileText className="w-4 h-4" /> {bulkPdfLoading ? '生成中...' : `PDF (${inspections.length}件)`}
                    </button>
                  </div>
                )}

                {/* 生成に時間がかかりユーザーアクティベーションが切れたときだけ出る。
                    navigator.shareはこのボタンのタップ自体を新しいアクティベーション
                    として使う（usePendingPdfShare参照） */}
                {pendingPdf && (
                  <button
                    onClick={sharePendingPdf}
                    className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" /> PDFを保存
                  </button>
                )}
                {inspections.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {searchLoading ? '' : '条件を指定して検索してください'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400">{inspections.length}件</p>
                    {inspections.map(insp => {
                      const companyName = companies.find(c => c.id === insp.company_id)?.name || '—';
                      const isSelected = selectedInspection?.id === insp.id;
                      return (
                        <div key={insp.id} className="bg-white rounded-lg border overflow-hidden">
                          <button className="w-full text-left p-3" onClick={() => setSelectedInspection(isSelected ? null : insp)}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {insp.serial != null
                                  ? <span className="font-mono text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{formatSerial(insp.serial)}</span>
                                  : <span className="font-mono text-xs text-slate-300">—</span>
                                }
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  insp.match_status === 'DANGER' ? 'bg-red-100 text-red-700' :
                                  insp.match_status === 'ERROR' ? 'bg-slate-100 text-slate-500' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {insp.match_status === 'DANGER' ? <AlertTriangle className="w-3 h-3 inline mr-0.5" /> : insp.match_status === 'SAFE' ? <CheckCircle className="w-3 h-3 inline mr-0.5" /> : null}
                                  {insp.match_status}
                                </span>
                                {isBlurryImage(insp.image_quality) && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                                    <ImageOff className="w-3 h-3" aria-hidden="true" />{BLURRY_IMAGE_LABEL}
                                  </span>
                                )}
                                {insp.image_mode && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${IMAGE_MODE_STYLE[insp.image_mode] ?? 'bg-slate-100 text-slate-500'}`}>
                                    {IMAGE_MODE_LABEL[insp.image_mode] ?? insp.image_mode}
                                  </span>
                                )}
                                {(() => { const fb = getLatestFeedback(insp.inspection_feedbacks); return fb ? (
                                  <span title={fb.comment || ''} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${FEEDBACK_STYLE[fb.verdict] ?? 'bg-slate-100 text-slate-500'}`}>
                                    {FEEDBACK_LABEL[fb.verdict] ?? fb.verdict}
                                  </span>
                                ) : null; })()}
                              </div>
                              <span className="text-xs text-slate-400 flex-shrink-0">
                                {new Date(insp.inspected_at).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-xs text-slate-600">{companyName} · {insp.store_name || '—'} · {insp.dept_name || '—'}</div>
                            <div className="text-xs text-slate-500 truncate">{insp.label_product_name || '—'}</div>
                          </button>
                          {isSelected && (
                            <div className="border-t p-3 bg-slate-50 space-y-2">
                              {insp.image_urls && insp.image_urls.length > 0 && (
                                <div className="space-y-1">
                                  <div className="flex gap-2 overflow-x-auto pb-1">
                                    {insp.image_urls.map((path, i) => (
                                      <StorageImage
                                        key={i}
                                        path={path}
                                        className="h-24 w-24 object-cover rounded border flex-shrink-0 cursor-pointer"
                                        alt={`img-${i}`}
                                        onClick={() => setZoomImage(path)}
                                      />
                                    ))}
                                  </div>
                                  <div className="flex gap-1">
                                    {insp.image_urls.map((path, i) => (
                                      <button key={i} onClick={() => downloadInspectionImage(path, insp.serial, i + 1)}
                                        className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100">
                                        DL {insp.serial}_{i + 1}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="text-xs space-y-1 text-slate-600">
                                <div><span className="text-slate-400">メール:</span> {insp.user_email || '—'}</div>
                                <div><span className="text-slate-400">ロット:</span> {insp.lot_number || '—'}</div>
                                <div><span className="text-slate-400">目視:</span> {insp.detected_food_visual || '—'}</div>
                                {insp.reason && <div><span className="text-slate-400">判定理由:</span> {insp.reason}</div>}
                                {insp.image_mode && (
                                  <div><span className="text-slate-400">検査モード:</span>{' '}
                                    <span className={`inline px-1.5 py-0.5 rounded text-[10px] font-medium ${IMAGE_MODE_STYLE[insp.image_mode] ?? ''}`}>
                                      {IMAGE_MODE_LABEL[insp.image_mode] ?? insp.image_mode}
                                    </span>
                                  </div>
                                )}
                                {insp.analyze_ms != null && (
                                  <div><span className="text-slate-400">判定時間:</span> <span className="font-mono">⏱ {(insp.analyze_ms / 1000).toFixed(2)}s</span></div>
                                )}
                                {insp.model_used && (
                                  <div><span className="text-slate-400">使用モデル:</span> <span className="font-mono">{insp.model_used}</span></div>
                                )}
                                {(insp.device_type || insp.browser_type) && (
                                  <div><span className="text-slate-400">端末:</span> {[insp.device_type, insp.browser_type].filter(Boolean).join(' / ')}</div>
                                )}
                                {insp.is_standalone != null && (
                                  <div><span className="text-slate-400">動作モード:</span> {insp.is_standalone ? '📱 ホーム画面から起動' : '🌐 ブラウザで表示'}</div>
                                )}
                                {insp.user_agent && (
                                  <div><span className="text-slate-400">User-Agent:</span> <span className="font-mono text-[10px] break-all">{insp.user_agent}</span></div>
                                )}
                                {insp.app_version && (
                                  <div><span className="text-slate-400">バージョン:</span> <span className="font-mono">{insp.app_version}</span></div>
                                )}
                                {(() => { const fb = getLatestFeedback(insp.inspection_feedbacks); return fb ? (
                                  <div><span className="text-slate-400">フィードバック:</span>{' '}
                                    <span className={`inline px-1.5 py-0.5 rounded text-[10px] font-medium ${FEEDBACK_STYLE[fb.verdict] ?? ''}`}>
                                      {FEEDBACK_LABEL[fb.verdict] ?? fb.verdict}
                                    </span>
                                    {fb.comment && <span className="ml-2 text-slate-500">「{fb.comment}」</span>}
                                  </div>
                                ) : null; })()}
                              </div>
                              <div className="flex gap-2 flex-wrap mt-1">
                                {insp.image_urls?.length > 0 && (
                                  <button onClick={() => openInspectionPdf(insp, { exporterEmail: profile?.email, onIOSPdfReady: shareOrDefer })} className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium flex items-center gap-1">
                                    <FileText className="w-3 h-3" /> PDF
                                  </button>
                                )}
                                <button onClick={() => { navigator.clipboard.writeText(JSON.stringify({ match_status: insp.match_status, label_product_name: insp.label_product_name, detected_food_visual: insp.detected_food_visual, reason: insp.reason }, null, 2)); }}
                                  className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">
                                  JSON
                                </button>
                                {insp.image_urls?.length > 0 && insp.company_id && (
                                  <RegisterReferenceButton inspection={insp} companyId={insp.company_id} />
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'regression' && (
              <RegressionTestTab companies={companies} />
            )}

            {tab === 'settings' && (
              <div className="space-y-3">
                <div className="bg-white rounded-lg border divide-y">
                  <div className="px-4 py-3"><p className="text-xs font-bold text-slate-500">ストレージ</p></div>
                  <div className="flex items-center justify-between px-4 py-4 gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">検査画像を Supabase Storage に保存する</div>
                      <div className="text-xs text-slate-400 mt-0.5">ONにすると、解析した画像が Storage に保存され、管理パネルから参照できます。OFFの場合は画像は保存されません（テキスト情報のみ記録）</div>
                    </div>
                    <Toggle value={settings.pm_image_storage_enabled === 'true' || settings.pm_image_storage_enabled === true} onChange={() => updateSetting('pm_image_storage_enabled', !(settings.pm_image_storage_enabled === 'true' || settings.pm_image_storage_enabled === true))} disabled={savingKey === 'pm_image_storage_enabled'} />
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>
      <ImageZoomOverlay path={zoomImage} onClose={() => setZoomImage(null)} />
    </div>
  );
}

export default SuperAdminPanel;
